import { useMemo, useRef, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { Button, Field, Modal, StatusBadge, type Column, type StatusTone } from "@fawkes/ui";
import { Euro, Gift, ImagePlus, PawPrint, Pencil, Plus, Search, Trash2 } from "lucide-react";

import { OverviewHeader } from "../components/OverviewHeader";
import { BtwKeuze, btwWeergave, huidigeBtwUitHistorie, useBtwCodes } from "../components/BtwKeuze";
import { formatDate, formatEuro, todayIso } from "../i18n/format";
import {
  EENHEDEN,
  NIVEAUS,
  leesFoto,
  prijsTekst,
  type Arrangement,
  type ArrangementEenheid,
  type ArrangementNiveau,
} from "./pensionarrangement/arrangement";
import "./PensionArrangementen.css";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

/**
 * Producten › Pension arrangementen: de arrangementen en extra's die de balie in de wizard Nieuwe reservering kiest,
 * zoals een parkwandeling, verwennerij of "Live volgen". Het pension richt ze hier zelf in — foto, omschrijving,
 * diersoort, per dier of voor de hele reservering, per keer, dag, week of verblijf — en zet de prijs, met btw en
 * vanaf een ingangsdatum, in dezelfde versieketen als de andere pensiontarieven.
 */

type T = (key: string, options?: { args?: unknown[] }) => string;

interface Soort {
  id: number;
  naam?: string;
  label?: string;
}

interface HistorieRegel {
  versieId: number;
  bedrag: number;
  effectdatum: string;
  einddatum: string | null;
  reden: string | null;
  btwCodeId: number | null;
  status: "GEPLAND" | "ACTIEF" | "VERVALLEN";
}

interface Concept {
  id: number | null;
  naam: string;
  omschrijving: string;
  foto: string | null;
  diersoortId: string;
  niveau: ArrangementNiveau;
  eenheid: ArrangementEenheid;
  vanaf: boolean;
  maxAantal: string;
  actief: boolean;
  positie: string;
  /** De prijs die bij het opslaan ingaat; leeg laat de prijs zoals hij is. */
  bedrag: string;
  effectdatum: string;
  btwCodeId: string;
}

const LEEG: Concept = {
  id: null,
  naam: "",
  omschrijving: "",
  foto: null,
  diersoortId: "",
  niveau: "DIER",
  eenheid: "PER_KEER",
  vanaf: false,
  maxAantal: "",
  actief: true,
  positie: "0",
  bedrag: "",
  effectdatum: todayIso(),
  btwCodeId: "",
};

const OMSCHRIJVING_MAX = 250;

const foutSleutel = (err: unknown) => {
  const code = (err as { statusCode?: number } | null)?.statusCode;
  return code === 403 ? "pensionArrangement.fout.recht" : code === 400 ? "pensionArrangement.fout.ongeldig" : "pensionArrangement.fout.algemeen";
};

export function PensionArrangementen() {
  const t = useTranslate() as T;
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading, isError, refetch } = useCustom<Arrangement[]>({ url: "pension/arrangementen", method: "get" });
  const soortenData = useCustom<Soort[]>({ url: "animals/reference/species", method: "get" }).data?.data;
  const soorten = (Array.isArray(soortenData) ? soortenData : []).map((s) => ({ id: s.id, naam: s.naam ?? s.label ?? String(s.id) }));
  const alle = useMemo(() => (Array.isArray(data?.data) ? data.data : []), [data]);

  const [zoek, setZoek] = useState("");
  const [niveau, setNiveau] = useState<ArrangementNiveau | null>(null);
  const [soort, setSoort] = useState("");
  const [inactief, setInactief] = useState(true);
  const [concept, setConcept] = useState<Concept | null>(null);
  const [prijsVan, setPrijsVan] = useState<Arrangement | null>(null);
  const [weg, setWeg] = useState<Arrangement | null>(null);

  const term = zoek.trim().toLowerCase();
  const zichtbaar = alle.filter(
    (a) =>
      (niveau == null || a.niveau === niveau) &&
      (soort === "" || String(a.diersoortId ?? "") === soort || (soort === "alle" && a.diersoortId == null)) &&
      (inactief || a.actief) &&
      (term === "" || a.naam.toLowerCase().includes(term) || (a.omschrijving ?? "").toLowerCase().includes(term)),
  );

  const bewerk = (a: Arrangement) =>
    setConcept({
      id: a.id,
      naam: a.naam,
      omschrijving: a.omschrijving ?? "",
      foto: a.foto,
      diersoortId: a.diersoortId == null ? "" : String(a.diersoortId),
      niveau: a.niveau,
      eenheid: a.eenheid,
      vanaf: a.vanaf,
      maxAantal: a.maxAantal == null ? "" : String(a.maxAantal),
      actief: a.actief,
      positie: String(a.positie),
      bedrag: "",
      effectdatum: todayIso(),
      btwCodeId: a.prijs?.btwCodeId == null ? "" : String(a.prijs.btwCodeId),
    });

  return (
    <div className="parr">
      <OverviewHeader
        icon={<Gift size={22} />}
        title={t("pensionArrangement.titel")}
        subtitle={t("pensionArrangement.subtitel")}
        right={
          <Button onClick={() => setConcept(LEEG)}>
            <Plus size={16} aria-hidden="true" /> {t("pensionArrangement.nieuw")}
          </Button>
        }
      />

      <div className="parr__balk">
        <label className="parr__zoek">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            autoComplete="off"
            value={zoek}
            onChange={(e) => setZoek(e.target.value)}
            placeholder={t("pensionArrangement.zoek")}
            aria-label={t("pensionArrangement.zoek")}
          />
        </label>
        <div className="parr__chips" role="group" aria-label={t("pensionArrangement.veld.niveau")}>
          <button type="button" className="parr__chip" aria-pressed={niveau == null} onClick={() => setNiveau(null)}>
            {t("pensionArrangement.alles")}
          </button>
          {NIVEAUS.map((n) => (
            <button key={n} type="button" className="parr__chip" aria-pressed={niveau === n} onClick={() => setNiveau(n)}>
              {t(`pensionArrangement.niveau.${n}`)}
            </button>
          ))}
        </div>
        <select className="parr__select" value={soort} onChange={(e) => setSoort(e.target.value)} aria-label={t("pensionArrangement.veld.diersoort")}>
          <option value="">{t("pensionArrangement.filter.elkeSoort")}</option>
          <option value="alle">{t("pensionArrangement.alleSoorten")}</option>
          {soorten.map((s) => (
            <option key={s.id} value={s.id}>
              {s.naam}
            </option>
          ))}
        </select>
        <label className="parr__vink">
          <input type="checkbox" checked={inactief} onChange={(e) => setInactief(e.target.checked)} />
          {t("pensionArrangement.filter.inactief")}
        </label>
      </div>

      {isError && (
        <p className="parr__melding" role="alert">
          {t("pensionArrangement.laadFout")}
        </p>
      )}
      {!isError && !isLoading && zichtbaar.length === 0 && (
        <div className="parr__leeg">
          <Gift size={32} aria-hidden="true" />
          <p>{alle.length === 0 ? t("pensionArrangement.leeg") : t("pensionArrangement.geenTreffers")}</p>
        </div>
      )}
      {isLoading && <p className="parr__uitleg">{t("common.loading")}</p>}

      <ul className="parr__raster">
        {zichtbaar.map((a) => (
          <li key={a.id} className="parr__kaart" data-inactief={!a.actief || undefined}>
            {a.foto ? (
              <img className="parr__foto" src={a.foto} alt="" />
            ) : (
              <span className="parr__foto parr__foto--leeg" aria-hidden="true">
                <ImagePlus size={28} />
              </span>
            )}
            <div className="parr__tekst">
              <h3>{a.naam}</h3>
              {a.omschrijving && <p className="parr__omschrijving">{a.omschrijving}</p>}
              <p className="parr__prijs">{prijsTekst(a, t, locale)}</p>
              <div className="parr__labels">
                <span className="parr__label">{t(`pensionArrangement.niveau.${a.niveau}`)}</span>
                <span className="parr__label">
                  <PawPrint size={12} aria-hidden="true" />
                  {a.diersoort ?? t("pensionArrangement.alleSoorten")}
                </span>
                {a.maxAantal != null && (
                  <span className="parr__label">{t("pensionArrangement.max", { args: [a.maxAantal] })}</span>
                )}
                {!a.actief && <span className="parr__label parr__label--uit">{t("pensionArrangement.inactief")}</span>}
              </div>
            </div>
            <div className="parr__acties">
              <button type="button" className="parr__actie" onClick={() => bewerk(a)} aria-label={t("pensionArrangement.bewerkVan", { args: [a.naam] })}>
                <Pencil size={15} aria-hidden="true" />
                {t("pensionArrangement.bewerk")}
              </button>
              <button type="button" className="parr__actie" onClick={() => setPrijsVan(a)} aria-label={t("pensionArrangement.prijsVan", { args: [a.naam] })}>
                <Euro size={15} aria-hidden="true" />
                {t("pensionArrangement.prijs.knop")}
              </button>
              <button
                type="button"
                className="parr__actie parr__actie--weg"
                onClick={() => setWeg(a)}
                aria-label={t("pensionArrangement.verwijderVan", { args: [a.naam] })}
              >
                <Trash2 size={15} aria-hidden="true" />
              </button>
            </div>
          </li>
        ))}
      </ul>

      {concept && (
        <ArrangementVenster
          concept={concept}
          soorten={soorten}
          t={t}
          locale={locale}
          onSluit={() => setConcept(null)}
          onOpgeslagen={(nieuw) => {
            setConcept(null);
            void refetch();
            if (nieuw && !nieuw.prijs) setPrijsVan(nieuw);
          }}
        />
      )}
      {prijsVan && (
        <PrijsVenster
          arrangement={prijsVan}
          t={t}
          locale={locale}
          onSluit={() => setPrijsVan(null)}
          onOpgeslagen={() => void refetch()}
        />
      )}
      {weg && <VerwijderVenster arrangement={weg} t={t} onSluit={() => setWeg(null)} onWeg={() => { setWeg(null); void refetch(); }} />}
    </div>
  );
}

/** De prijzen van een arrangement, nieuwste bovenaan. */
function usePrijshistorie(id: number | null) {
  const { data, isLoading, refetch } = useCustom<HistorieRegel[]>({
    url: `pension/arrangementen/${id ?? 0}/historie`,
    method: "get",
    queryOptions: { enabled: id != null },
  });
  const rijen = useMemo(() => {
    const lijst = Array.isArray(data?.data) ? [...data.data] : [];
    return lijst.sort((a, b) => b.effectdatum.localeCompare(a.effectdatum));
  }, [data]);
  return { rijen, isLoading, refetch };
}

function prijsKolommen(t: T, locale: string, btwCodes: { id: number; label: string; percentage: number | null }[]): Column<HistorieRegel>[] {
  return [
    { header: t("pensionTarief.hist.vanaf"), cell: (r) => formatDate(r.effectdatum, locale) },
    { header: t("pensionTarief.hist.tot"), cell: (r) => formatDate(r.einddatum, locale) },
    { header: t("pensionArrangement.veld.bedrag"), align: "right", cell: (r) => formatEuro(r.bedrag, locale) },
    { header: t("btw.kolom"), align: "right", cell: (r) => btwWeergave(btwCodes, r.btwCodeId) },
    {
      header: t("pensionTarief.hist.status"),
      cell: (r) => <StatusBadge tone={toon(r.status)}>{t(`pensionTarief.hist.status.${r.status}`)}</StatusBadge>,
    },
  ];
}

function ArrangementVenster({
  concept: start,
  soorten,
  t,
  locale,
  onSluit,
  onOpgeslagen,
}: {
  concept: Concept;
  soorten: { id: number; naam: string }[];
  t: T;
  locale: string;
  onSluit: () => void;
  onOpgeslagen: (nieuw: Arrangement | null) => void;
}) {
  const [c, setC] = useState<Concept>(start);
  const btwCodes = useBtwCodes();
  const historie = usePrijshistorie(start.id);
  const [fout, setFout] = useState<string | null>(null);
  const [geprobeerd, setGeprobeerd] = useState(false);
  const invoer = useRef<HTMLInputElement | null>(null);
  const { mutateAsync, isLoading } = useCustomMutation<Arrangement>();
  const zet = <K extends keyof Concept>(k: K, v: Concept[K]) => setC((x) => ({ ...x, [k]: v }));

  const kiesFoto = async (bestand: File | undefined) => {
    if (!bestand) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(bestand.type)) {
      setFout(t("pensionArrangement.fout.foto"));
      return;
    }
    setFout(null);
    zet("foto", await leesFoto(bestand));
  };

  const bedrag = getal(c.bedrag);
  const prijsMist = c.bedrag.trim() !== "" && (bedrag == null || c.effectdatum === "" || c.btwCodeId === "");

  const bewaar = async () => {
    setGeprobeerd(true);
    if (c.naam.trim() === "") return;
    if (prijsMist) {
      setFout(t(bedrag == null ? "pensionArrangement.fout.bedrag" : c.btwCodeId === "" ? "btw.verplicht" : "pensionArrangement.fout.bedrag"));
      return;
    }
    setFout(null);
    try {
      const res = await mutateAsync({
        url: c.id == null ? "pension/arrangementen" : `pension/arrangementen/${c.id}`,
        method: c.id == null ? "post" : "put",
        values: {
          naam: c.naam.trim(),
          omschrijving: c.omschrijving.trim() === "" ? null : c.omschrijving.trim(),
          foto: c.foto,
          diersoortId: c.diersoortId === "" ? null : Number(c.diersoortId),
          niveau: c.niveau,
          eenheid: c.eenheid,
          vanaf: c.vanaf,
          maxAantal: c.maxAantal.trim() === "" ? null : Number(c.maxAantal),
          actief: c.actief,
          positie: c.positie.trim() === "" ? 0 : Number(c.positie),
        },
        successNotification: false,
        errorNotification: false,
      });
      const id = c.id ?? res.data?.id ?? null;
      if (bedrag != null && id != null) {
        await mutateAsync({
          url: `pension/arrangementen/${id}/prijswijziging`,
          method: "post",
          values: { effectdatum: c.effectdatum, bedrag, btwCodeId: Number(c.btwCodeId), reden: null },
          successNotification: false,
          errorNotification: false,
        });
      }
      onOpgeslagen(c.id == null ? (res.data ?? null) : null);
    } catch (err) {
      const code = (err as { statusCode?: number } | null)?.statusCode;
      setFout(t(code === 403 && c.effectdatum < todayIso() ? "pensionArrangement.fout.verleden" : foutSleutel(err)));
    }
  };

  const titel = c.id == null ? t("pensionArrangement.venster.nieuw") : t("pensionArrangement.venster.bewerk", { args: [start.naam] });
  return (
    <Modal
      open
      size="wide"
      title={titel}
      onClose={onSluit}
      footer={
        <>
          {fout && (
            <span className="fawkes-error" role="alert">
              {fout}
            </span>
          )}
          <Button variant="secondary" onClick={onSluit}>
            {t("common.cancel")}
          </Button>
          <Button onClick={() => void bewaar()} disabled={isLoading}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form
        className="parr-form"
        aria-label={titel}
        onSubmit={(e) => {
          e.preventDefault();
          void bewaar();
        }}
      >
        <div className="parr-form__velden">
          <Field label={t("pensionArrangement.veld.naam")} value={c.naam} maxLength={80} required onChange={(e) => zet("naam", e.target.value)} />
          {geprobeerd && c.naam.trim() === "" && <p className="parr__melding">{t("pensionArrangement.fout.naam")}</p>}
          <div className="parr-form__veld">
            <label htmlFor="parr-omschrijving">{t("pensionArrangement.veld.omschrijving")}</label>
            <textarea
              id="parr-omschrijving"
              rows={3}
              maxLength={OMSCHRIJVING_MAX}
              value={c.omschrijving}
              onChange={(e) => zet("omschrijving", e.target.value)}
            />
            <small aria-hidden="true">
              {c.omschrijving.length}/{OMSCHRIJVING_MAX}
            </small>
          </div>
          <div className="parr-form__twee">
            <label className="parr-form__veld">
              <span>{t("pensionArrangement.veld.diersoort")}</span>
              <select value={c.diersoortId} onChange={(e) => zet("diersoortId", e.target.value)}>
                <option value="">{t("pensionArrangement.alleSoorten")}</option>
                {soorten.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.naam}
                  </option>
                ))}
              </select>
            </label>
            <label className="parr-form__veld">
              <span>{t("pensionArrangement.veld.eenheid")}</span>
              <select value={c.eenheid} onChange={(e) => zet("eenheid", e.target.value as ArrangementEenheid)}>
                {EENHEDEN.map((e) => (
                  <option key={e} value={e}>
                    {t(`pensionArrangement.eenheid.${e}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <fieldset className="parr-form__groep">
            <legend>{t("pensionArrangement.veld.niveau")}</legend>
            {NIVEAUS.map((n) => (
              <label key={n} className="parr__vink">
                <input type="radio" name="niveau" checked={c.niveau === n} onChange={() => zet("niveau", n)} />
                <span>
                  <strong>{t(`pensionArrangement.niveau.${n}`)}</strong> — {t(`pensionArrangement.niveau.${n}.uitleg`)}
                </span>
              </label>
            ))}
          </fieldset>
          <div className="parr-form__twee">
            <Field
              label={t("pensionArrangement.veld.maxAantal")}
              type="number"
              min={1}
              value={c.maxAantal}
              onChange={(e) => zet("maxAantal", e.target.value)}
            />
            <Field label={t("pensionArrangement.veld.positie")} type="number" value={c.positie} onChange={(e) => zet("positie", e.target.value)} />
          </div>
          <fieldset className="parr-form__groep">
            <legend>{t("pensionArrangement.prijs.legend")}</legend>
            <p className="parr__uitleg">{t(c.id == null ? "pensionArrangement.prijs.nieuwUitleg" : "pensionArrangement.prijs.wijzigUitleg")}</p>
            <div className="parr-form__prijs">
              <Field
                label={t("pensionArrangement.veld.bedrag")}
                inputMode="decimal"
                value={c.bedrag}
                placeholder={t("pensionArrangement.prijs.bedragPlaats")}
                onChange={(e) => zet("bedrag", e.target.value)}
              />
              <Field
                label={t("pensionArrangement.veld.effectdatum")}
                type="date"
                value={c.effectdatum}
                onChange={(e) => zet("effectdatum", e.target.value)}
              />
              <BtwKeuze
                value={c.btwCodeId}
                onChange={(v) => zet("btwCodeId", v)}
                codes={btwCodes}
                missing={geprobeerd && c.bedrag.trim() !== "" && c.btwCodeId === ""}
                t={t}
              />
            </div>
            <small className="parr__uitleg">{t("pensionArrangement.prijs.velduitleg")}</small>
            {c.id != null && (
              <IndeelbareTabel tabelKey="pension-arrangementen-1"
                columns={prijsKolommen(t, locale, btwCodes)}
                rows={historie.rijen}
                getRowKey={(r) => String(r.versieId)}
                empty={historie.isLoading ? t("common.loading") : t("pensionArrangement.prijs.geenHistorie")}
              />
            )}
          </fieldset>
          <label className="parr__vink">
            <input type="checkbox" checked={c.actief} onChange={(e) => zet("actief", e.target.checked)} />
            {t("pensionArrangement.veld.actief")}
          </label>
        </div>
        <div className="parr-form__foto">
          <span className="parr-form__label">{t("pensionArrangement.veld.foto")}</span>
          <input
            ref={invoer}
            className="parr__verborgen"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            aria-label={t("pensionArrangement.foto.kies")}
            tabIndex={-1}
            onChange={(e) => {
              void kiesFoto(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <button type="button" className="parr-form__upload" onClick={() => invoer.current?.click()}>
            {c.foto ? <img src={c.foto} alt={t("pensionArrangement.foto.voorbeeld")} /> : <ImagePlus size={32} aria-hidden="true" />}
            <span>{c.foto ? t("pensionArrangement.foto.anders") : t("pensionArrangement.foto.kies")}</span>
          </button>
          {c.foto && (
            <button type="button" className="parr__actie" onClick={() => zet("foto", null)}>
              <Trash2 size={15} aria-hidden="true" />
              {t("pensionArrangement.foto.weg")}
            </button>
          )}
          <small className="parr__uitleg">{t("pensionArrangement.foto.uitleg")}</small>
        </div>
      </form>
    </Modal>
  );
}

interface PrijsRegel {
  effectdatum: string;
  bedrag: string;
  btwCodeId: string;
}

const getal = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const toon = (status: HistorieRegel["status"]): StatusTone =>
  status === "ACTIEF" ? "success" : status === "GEPLAND" ? "warning" : "neutral";

function PrijsVenster({
  arrangement: a,
  t,
  locale,
  onSluit,
  onOpgeslagen,
}: {
  arrangement: Arrangement;
  t: T;
  locale: string;
  onSluit: () => void;
  onOpgeslagen: () => void;
}) {
  const { rijen: historie, isLoading, refetch } = usePrijshistorie(a.id);
  const btwCodes = useBtwCodes();
  const huidigeBtw = huidigeBtwUitHistorie(historie);
  const [regel, setRegel] = useState<PrijsRegel>({ effectdatum: todayIso(), bedrag: "", btwCodeId: "" });
  const [fout, setFout] = useState<string | null>(null);
  const [opgeslagen, setOpgeslagen] = useState(false);
  const { mutateAsync, isLoading: bezig } = useCustomMutation();
  const btw = regel.btwCodeId || huidigeBtw;

  const bewaar = async () => {
    setOpgeslagen(false);
    const bedrag = getal(regel.bedrag);
    if (bedrag == null || regel.effectdatum === "") {
      setFout(t("pensionArrangement.fout.bedrag"));
      return;
    }
    if (btw === "") {
      setFout(t("btw.verplicht"));
      return;
    }
    setFout(null);
    try {
      await mutateAsync({
        url: `pension/arrangementen/${a.id}/prijswijziging`,
        method: "post",
        values: {
          effectdatum: regel.effectdatum,
          bedrag,
          btwCodeId: Number(btw),
          reden: null,
        },
        successNotification: false,
        errorNotification: false,
      });
      setRegel({ effectdatum: todayIso(), bedrag: "", btwCodeId: "" });
      setOpgeslagen(true);
      void refetch();
      onOpgeslagen();
    } catch (err) {
      const code = (err as { statusCode?: number } | null)?.statusCode;
      setFout(t(code === 403 && regel.effectdatum < todayIso() ? "pensionArrangement.fout.verleden" : foutSleutel(err)));
    }
  };

  return (
    <Modal
      open
      size="wide"
      title={t("pensionArrangement.prijs.titel", { args: [a.naam] })}
      onClose={onSluit}
      footer={
        <Button variant="secondary" onClick={onSluit}>
          {t("common.close")}
        </Button>
      }
    >
      <div className="parr-prijs">
        <p className="parr-prijs__huidig">
          {t("pensionArrangement.prijs.huidig")} <strong>{prijsTekst(a, t, locale)}</strong>
          {a.prijs?.geldigVanaf && <> · {t("pensionArrangement.prijs.sinds", { args: [formatDate(a.prijs.geldigVanaf, locale)] })}</>}
        </p>
        <div className="parr-prijs__regel">
          <Field
            label={t("pensionArrangement.veld.effectdatum")}
            type="date"
            value={regel.effectdatum}
            onChange={(e) => setRegel((r) => ({ ...r, effectdatum: e.target.value }))}
          />
          <Field
            label={t("pensionArrangement.veld.bedrag")}
            inputMode="decimal"
            value={regel.bedrag}
            onChange={(e) => setRegel((r) => ({ ...r, bedrag: e.target.value }))}
          />
          <BtwKeuze value={btw} onChange={(v) => setRegel((r) => ({ ...r, btwCodeId: v }))} codes={btwCodes} missing={false} t={t} />
          <Button onClick={() => void bewaar()} disabled={bezig}>
            {t("pensionArrangement.prijs.opslaan")}
          </Button>
        </div>
        {fout && (
          <p className="parr__melding" role="alert">
            {fout}
          </p>
        )}
        {opgeslagen && (
          <p className="parr__gelukt" role="status">
            {t("pensionArrangement.prijs.opgeslagen")}
          </p>
        )}
        <IndeelbareTabel tabelKey="pension-arrangementen-2"
          columns={prijsKolommen(t, locale, btwCodes)}
          rows={historie}
          getRowKey={(r) => String(r.versieId)}
          empty={isLoading ? t("common.loading") : t("pensionTarief.hist.empty")}
        />
      </div>
    </Modal>
  );
}

function VerwijderVenster({ arrangement: a, t, onSluit, onWeg }: { arrangement: Arrangement; t: T; onSluit: () => void; onWeg: () => void }) {
  const { mutateAsync, isLoading } = useCustomMutation();
  const [fout, setFout] = useState<string | null>(null);
  const weg = async () => {
    try {
      await mutateAsync({ url: `pension/arrangementen/${a.id}`, method: "delete", values: {}, successNotification: false, errorNotification: false });
      onWeg();
    } catch (err) {
      setFout(t(foutSleutel(err)));
    }
  };
  return (
    <Modal
      open
      title={t("pensionArrangement.verwijder.titel", { args: [a.naam] })}
      onClose={onSluit}
      footer={
        <>
          {fout && (
            <span className="fawkes-error" role="alert">
              {fout}
            </span>
          )}
          <Button variant="secondary" onClick={onSluit}>
            {t("common.cancel")}
          </Button>
          <Button onClick={() => void weg()} disabled={isLoading}>
            {t("pensionArrangement.verwijder.bevestig")}
          </Button>
        </>
      }
    >
      <p>{t("pensionArrangement.verwijder.uitleg")}</p>
    </Modal>
  );
}
